import { ReactNode, CSSProperties, ButtonHTMLAttributes } from 'react';

enum ButtonTypeEnum {
  Contained = 'contained',
  Outlined = 'outlined',
  Warning = 'warning',
  Danger = 'danger',
  Info = 'info',
  Light = 'light',
  Dark = 'dark',
  Success = 'success',
}

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  endIcon?: ReactNode;
  buttonType:
    | 'contained'
    | 'outlined'
    | 'warning'
    | 'danger'
    | 'info'
    | 'light'
    | 'dark'
    | 'success';
  startIcon?: ReactNode;
  onClickHandler: (prop?: any) => void;
  style?: CSSProperties;
  loading?: boolean;
  loadingText?: string;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  children,
  onClickHandler,
  endIcon,
  startIcon,
  style,
  buttonType,
  loading = false,
  loadingText,
  disabled,
  ...rest
}) => {
  const btnTypeHandler = (type: string) => {
    switch (type) {
      case ButtonTypeEnum.Contained:
        return 'btn-primary';
      case ButtonTypeEnum.Outlined:
        return 'btn-outline-primary';
      case ButtonTypeEnum.Warning:
        return 'btn-warning';
      case ButtonTypeEnum.Success:
        return 'btn-success';
      case ButtonTypeEnum.Danger:
        return 'btn-danger';
      case ButtonTypeEnum.Info:
        return 'btn-info';
      case ButtonTypeEnum.Light:
        return 'btn-light';
      case ButtonTypeEnum.Dark:
        return 'btn-dark';
      default:
        return 'btn-primary';
    }
  };

  // Circular loader component
  const CircularLoader = () => (
    <span
      className="spinner-border spinner-border-sm"
      role="status"
      aria-hidden="true"
      style={{
        width: '1rem',
        height: '1rem',
        marginRight: '8px',
      }}
    />
  );

  // Determine if button should be disabled
  const isDisabled = loading || disabled;

  // Handle click - prevent clicks when loading
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClickHandler(event);
  };

  return (
    <button
      className={`btn ${btnTypeHandler(buttonType)} btn-with-icon ${
        loading ? 'loading' : ''
      }`}
      onClick={handleClick}
      disabled={isDisabled}
      style={{
        ...style,
        position: 'relative',
        cursor: loading ? 'not-allowed' : disabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.65 : 1,
      }}
      {...rest}
    >
      {loading && <CircularLoader />}
      {!loading && startIcon && (
        <span className="leftIconButton">{startIcon}</span>
      )}

      <span style={{ opacity: loading ? 0.7 : 1 }}>
        {loading ? loadingText || 'Loading...' : children}
      </span>

      {!loading && endIcon && (
        <span className="rightIconButton">{endIcon}</span>
      )}
    </button>
  );
};

export default CustomButton;

/* 
Usage Examples:

// Basic loading state
<CustomButton
  buttonType="outlined"
  loading={isDownloadLoading}
  onClickHandler={() => {
    setCsvType('detailed');
    downloadCsvHandler();
  }}
  endIcon={<DownloadIcon />}
>
  Detailed Sheet
</CustomButton>

// With custom loading text
<CustomButton
  buttonType="contained"
  loading={isDownloadLoading}
  loadingText="Downloading..."
  onClickHandler={() => {
    setCsvType('detailed');
    downloadCsvHandler();
  }}
  endIcon={<DownloadIcon />}
>
  Detailed Sheet
</CustomButton>

// Multiple states
<CustomButton
  buttonType="success"
  loading={isProcessing}
  disabled={!hasData}
  loadingText="Processing..."
  onClickHandler={handleSubmit}
>
  Submit
</CustomButton>
*/
